/**
 * 分身任务失败归因(0829 走查 P0)。
 *
 * <p>真机实据:失败详情页把火山引擎报文**整段**打给用户(`b'{"ResponseMetadata":{"Action":
 * "JimengRealmanAvatarPictureOmniV15SubmitTask",…"RequestId":"2026…"}}'`)。视频舱同位置给的是
 * 「换个说法就能过 + 本次没有扣积分」,差别在于视频侧后端翻好了人话,分身侧直接透传上游。
 *
 * <p>★为什么用「是不是机器报文」而不是「在不在白名单里」做判据:白名单只放行认识的错误码,
 * 会把后端**已经翻好的中文人话**一起吞掉(那是比报文更有用的信息);黑名单(含 ResponseMetadata
 * 就隐藏)则换一种上游报文就漏。所以判据是**这段文本长得像不像机器结构**——像就不上屏,
 * 不像(中文人话)就原样透传。新上游换了报文格式,只要它仍是 JSON,这条判据依然拦得住。
 */
import { maskVendor } from "@/shared/lib/vendorMask";

/** 归因结果:`headline`/`hint` 是允许上屏的,`raw` 只给 console。 */
export interface DhFailureExplanation {
  /** 上屏主文案。恒非空。 */
  headline: string;
  /** 上屏副文案(下一步怎么办);没有可省。 */
  hint?: string;
  /** 原始 errorMessage,原样保留 —— 排查要用,但**不许进 DOM**。 */
  raw: string | null;
}

const FALLBACK = "生成失败,可重新创作一条。";

/**
 * 机器报文特征。命中任意一条即判为「不可上屏」。
 * 只认**结构**不认具体字段名 —— 换个上游、换个字段名依然拦得住。
 */
function looksLikeMachinePayload(text: string): boolean {
  return (
    /\bb'\{/.test(text) || // Python bytes 字面量(本次实据)
    /\{\s*"/.test(text) || // JSON 对象起手
    /"\s*:\s*[{["]/.test(text) // 任意 "key": {  / [  / "
  );
}

/**
 * 已知错误码 → 人话。key 是在报文里能稳定找到的**码**,不是整句 message
 * (message 文案上游随时会改,码不会)。
 */
const CODE_EXPLANATIONS: { match: RegExp; headline: string; hint: string }[] = [
  {
    // 1011 算法备案:平台敏感词库拦截(api CONTENT_SENSITIVE),与同步提交的弹窗同一文案
    match: /CONTENT_SENSITIVE|内容包含敏感信息/,
    headline: "内容包含敏感信息,无法生成",
    hint: "请修改输入后重试",
  },
  {
    // 50430 = Request Has Reached API Concurrent Limit
    match: /\b50430\b/,
    headline: "上游生成服务当前排队已满,这一条没能挤进去。",
    hint: "稍等一两分钟再试一次即可,不用改稿。",
  },
  {
    // 内容审核类(火山 InputTextSensitive / OutputAudioSensitive 等)
    match: /Sensitive|PolicyViolation|ContentDetected/i,
    headline: "文案或画面没通过内容审核。",
    hint: "把绝对化用词、疗效承诺、竞品名去掉,多半就能过。",
  },
  {
    match: /\b(50411|InvalidParameter|IllegalArgument)\b/i,
    headline: "这次提交的参数上游不接受(多半是形象照或文案不合规格)。",
    hint: "换一张正脸清晰照、或把文案改短一点再出一次。",
  },
  {
    match: /\b(timeout|timed out|deadline)\b/i,
    headline: "上游生成超时,这一条没出来。",
    hint: "原样重试一次通常就好;文案很长时可以拆成两条。",
  },
];

/** 后端常把「本次没有扣积分」拼在报文尾巴上 —— 这句是用户最关心的,要从报文里救出来。 */
function extractBillingNote(text: string): string | null {
  const m = text.match(/(本次[^。]*扣[^。]*积分[^。]*。?)/);
  return m ? m[1].trim() : null;
}

/**
 * 把后端给的 `errorMessage` 变成可以上屏的文案。
 *
 * - 中文人话 → 原样透传(后端翻好的比我猜的准)
 * - 机器报文 → 按错误码归因;认不出就退通用兜底。**任何情况下原文都不上屏**
 */
export function explainDhFailure(errorMessage: string | null | undefined): DhFailureExplanation {
  const raw = errorMessage ?? null;
  const text = (errorMessage ?? "").trim();
  if (!text) return { headline: FALLBACK, raw };
  // 1011:敏感词拦截无论是裸码还是报文,都统一成同一句(与提交时 422 弹窗一致)
  if (/\bCONTENT_SENSITIVE\b/.test(text)) {
    return { headline: "内容包含敏感信息,无法生成", hint: "请修改输入后重试", raw };
  }

  if (!looksLikeMachinePayload(text)) {
    // 后端已经给了人话(哪怕里面夹着英文音色名),原样用。
    return { headline: maskVendor(text), raw };
  }

  const billing = extractBillingNote(text);
  const hit = CODE_EXPLANATIONS.find((e) => e.match.test(text));
  if (hit) {
    return {
      headline: billing ? `${hit.headline} —— ${billing}` : hit.headline,
      hint: hit.hint,
      raw,
    };
  }
  return {
    headline: billing ? `${FALLBACK} —— ${billing}` : FALLBACK,
    hint: "多试一次通常就好;连续失败请把任务号发给管理员。",
    raw,
  };
}
